import {
	FunctionComponent,
	ReactElement,
	ReactNode,
	createContext,
	useCallback,
	useMemo,
	useState,
} from 'react';
import { DateFilter } from '@/features/dashboard/types';
import { CustomDateRange } from '@/shared/types/date';
import { SelectedLocation } from '@/features/dashboard/widgets/completed-cycles/filters/LocationFiltersDialog';
import { FilterState, getDefaultFilterState } from './defaults';

export interface IFilterContext extends FilterState {
	setSelectedSiteId: (siteId: string | undefined) => void;
	setCustomerId: (customerId: string | undefined) => void;
	setDateFilter: (dateFilter: DateFilter) => void;
	setCustomRangeFilter: (range: CustomDateRange) => void;
	setLocationsFilter: (locations: SelectedLocation[]) => void;
	resetFilters: () => void;
}

const noop = () => {};

export const FilterContext = createContext<IFilterContext>({
	...getDefaultFilterState(),
	setSelectedSiteId: noop,
	setCustomerId: noop,
	setDateFilter: noop,
	setCustomRangeFilter: noop,
	setLocationsFilter: noop,
	resetFilters: noop,
});

/** Session-only (in memory) site and timeframe selection shared by Dashboard and Logs. */
export const FilterProvider: FunctionComponent<{ children: ReactNode }> = ({
	children,
}): ReactElement => {
	const [state, setState] = useState<FilterState>(getDefaultFilterState);

	const patch = useCallback(
		<K extends keyof FilterState>(key: K, value: FilterState[K]) =>
			setState((prev) => ({ ...prev, [key]: value })),
		[]
	);

	const value = useMemo<IFilterContext>(
		() => ({
			...state,
			setSelectedSiteId: (v) => patch('selectedSiteId', v),
			setCustomerId: (v) => patch('customerId', v),
			setDateFilter: (v) => patch('dateFilter', v),
			setCustomRangeFilter: (v) => patch('customRangeFilter', v),
			setLocationsFilter: (v) => patch('locationsFilter', v),
			resetFilters: () => setState(getDefaultFilterState()),
		}),
		[state, patch]
	);

	return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
};
